import React, { useEffect, useState, useRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { Lesson } from '../../src/services/lessonsService';
import { toLocal, ymdLocal } from '../../src/utils/datetime';
import { LessonChip } from './LessonChip';
import { CalendarViewSelector } from './CalendarViewSelector';
// ADDED: Index-based color palette for lesson chips
const LESSON_CHIP_COLORS = ['#A78BFA', '#6EE7B7', '#7DD3FC', '#FCA5A5', '#5EEAD4', '#FCD34D', '#FDA4AF', '#A5B4FC' // Indigo
];
// ADDED: Helper function to get lesson chip color by index
const getLessonChipColor = (index: number): string => {
  return LESSON_CHIP_COLORS[index % LESSON_CHIP_COLORS.length];
};
type CalendarView = 'month' | 'week' | 'day';
interface TeacherCalendarDayProps {
  selectedDate: Date;
  lessons: Lesson[];
  isDark: boolean;
  onTimeSlotClick: (date: Date, hour: number, minute: number) => void;
  onLessonClick: (lesson: Lesson, e: React.MouseEvent) => void;
  startHour?: number;
  endHour?: number;
  onViewChange: (view: CalendarView) => void;
  onPrevDay: () => void;
  onNextDay: () => void;
  onTodayClick: () => void;
  popoverEvent?: Lesson | null; // ADDED: Receive popover state from parent
  onClosePopover?: () => void; // ADDED: Receive close function from parent
}
export const TeacherCalendarDay: React.FC<TeacherCalendarDayProps> = ({
  selectedDate,
  lessons,
  isDark,
  onTimeSlotClick,
  onLessonClick,
  startHour = 7,
  endHour = 21,
  onViewChange,
  onPrevDay,
  onNextDay,
  onTodayClick,
  popoverEvent,
  onClosePopover
}) => {
  const [currentTimePosition, setCurrentTimePosition] = useState<number | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const timeGridRef = useRef<HTMLDivElement>(null);
  const hourHeight = 60; // Height in pixels for each hour in the grid
  // Check if date is today
  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  };
  // Update current time indicator position
  useEffect(() => {
    const updateTimeIndicator = () => {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      if (hours >= startHour && hours <= endHour) {
        const hourPosition = (hours - startHour) * hourHeight;
        const minutePosition = minutes / 60 * hourHeight;
        setCurrentTimePosition(hourPosition + minutePosition);
      } else {
        setCurrentTimePosition(null);
      }
    };
    // Update immediately and then every minute
    updateTimeIndicator();
    const interval = setInterval(updateTimeIndicator, 60000);
    return () => clearInterval(interval);
  }, [startHour, endHour]);
  // Scroll to current time on initial render
  useEffect(() => {
    if (scrollContainerRef.current && currentTimePosition !== null) {
      const scrollPosition = currentTimePosition - window.innerHeight / 3;
      scrollContainerRef.current.scrollTop = Math.max(0, scrollPosition);
    }
  }, []);
  // Generate time slots
  const timeSlots = [];
  for (let hour = startHour; hour <= endHour; hour++) {
    timeSlots.push(hour);
  }
  // Get formatted time string (12-hour format)
  const getFormattedTime = (hour: number): string => {
    const suffix = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 === 0 ? 12 : hour % 12;
    return `${displayHour}:00 ${suffix}`;
  };
  // Format date for display
  const formatDateHeader = (): string => {
    return selectedDate.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };
  // Position lessons in the grid
  const renderLessons = () => {
    const lessonElements: JSX.Element[] = [];
    // FIXED: Use ymdLocal for timezone-safe date comparison (matches Week view)
    const selectedDateKey = ymdLocal(selectedDate.toISOString(), 'America/Los_Angeles');
    // ADDED: Sort lessons by start time to assign colors by chronological order
    const sortedLessons = [...lessons].sort((a, b) => {
      const aTime = toLocal(a.startAt, 'America/Los_Angeles');
      const bTime = toLocal(b.startAt, 'America/Los_Angeles');
      return aTime.getTime() - bTime.getTime();
    });
    // ADDED: Track lesson index for color assignment
    let lessonIndex = 0;
    sortedLessons.forEach(lesson => {
      try {
        // Convert UTC times to local for display
        const startTime = toLocal(lesson.startAt, 'America/Los_Angeles');
        const endTime = toLocal(lesson.endAt, 'America/Los_Angeles');
        // FIXED: Check if the lesson is on the selected date using ymdLocal (timezone-safe)
        const lessonDateKey = ymdLocal(lesson.startAt, 'America/Los_Angeles');
        if (lessonDateKey !== selectedDateKey) {
          return; // Skip this lesson
        }
        // Calculate position and height
        const startHourDecimal = startTime.getHours() + startTime.getMinutes() / 60;
        const endHourDecimal = endTime.getHours() + endTime.getMinutes() / 60;
        // Skip if outside our time range
        if (endHourDecimal < startHour || startHourDecimal > endHour) {
          return;
        }
        // FIXED: Account for border accumulation (1px border-t per hour cell)
        // Each hour cell has a 1px top border, so we need to add the cumulative border height
        const bordersAbove = Math.floor(startHourDecimal - startHour);
        // FIXED: Add vertical padding to center the lesson card within its time slot
        const verticalPadding = 4; // 4px padding on top and bottom for visual centering
        const top = Math.max(0, (startHourDecimal - startHour) * hourHeight + bordersAbove + verticalPadding);
        const height = Math.min((endHour - startHourDecimal + 1) * hourHeight, (endHourDecimal - startHourDecimal) * hourHeight) - verticalPadding * 2; // Subtract padding from both top and bottom
        // Extract time in HH:MM format
        const startTimeStr = `${startTime.getHours().toString().padStart(2, '0')}:${startTime.getMinutes().toString().padStart(2, '0')}`;
        const endTimeStr = `${endTime.getHours().toString().padStart(2, '0')}:${endTime.getMinutes().toString().padStart(2, '0')}`;
        // UPDATED: Use index-based color instead of instrument color
        const bgColor = getLessonChipColor(lessonIndex);
        // ADDED: Increment lesson index for next lesson
        lessonIndex++;
        // Check if this is a temporary lesson (optimistic update)
        const isTemporary = lesson.id.startsWith('temp_');
        lessonElements.push(<div key={lesson.id} className="absolute z-10 pointer-events-auto" style={{
          top: `${top}px`,
          height: `${height}px`,
          left: '8px',
          right: '8px'
        }} data-id="element-661">
            <LessonChip id={lesson.id} title={lesson.title || 'Lesson'} startTime={startTimeStr} endTime={endTimeStr} studentName={lesson.studentName || 'Student'} instrument={lesson.instrument} color={bgColor} status={lesson.status} variant="compact" onClick={(id, e) => onLessonClick(lesson, e)} isTemporary={isTemporary} className="h-full w-full" data-id="element-662" />
          </div>);
      } catch (err) {
        console.error('Error rendering lesson:', err);
      }
    });
    return lessonElements;
  };
  const isCurrentDay = isToday(selectedDate);
  return <div className="flex flex-col h-full" data-id="element-663">
      {/* Calendar Header */}
      <div className="mb-4" data-id="element-664">
        {/* Desktop layout */}
        <div className="hidden md:flex justify-between items-center" data-id="element-665">
          <div className="flex items-center space-x-3" data-id="element-666">
            <button onClick={onTodayClick} className={`px-4 py-2 rounded-md text-sm border-2 transition-colors ${isDark ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5'}`} data-id="element-667">
              Today
            </button>
            <div className="flex items-center" data-id="element-668">
              <button onClick={onPrevDay} className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`} aria-label="Previous day" data-id="element-669">
                <ChevronLeftIcon className="h-5 w-5" data-id="element-670" />
              </button>
              <span className={`text-lg font-medium mx-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-671">
                {formatDateHeader()}
              </span>
              <button onClick={onNextDay} className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`} aria-label="Next day" data-id="element-672">
                <ChevronRightIcon className="h-5 w-5" data-id="element-673" />
              </button>
            </div>
          </div>
          <div className="flex gap-2" data-id="element-674">
            <CalendarViewSelector currentView="day" onViewChange={onViewChange} isDark={isDark} availableViews={['day', 'week', 'month']} data-id="element-675" />
          </div>
        </div>

        {/* Mobile layout */}
        <div className="md:hidden flex flex-col space-y-3" data-id="element-676">
          {/* First row: Navigation arrows + Date (single line, centered) */}
          <div className="flex items-center justify-center" data-id="element-677">
            <button onClick={onPrevDay} className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`} aria-label="Previous day" data-id="element-678">
              <ChevronLeftIcon className="h-5 w-5" data-id="element-679" />
            </button>
            <span className={`text-base font-medium mx-3 text-center ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-680">
              {formatDateHeader()}
            </span>
            <button onClick={onNextDay} className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`} aria-label="Next day" data-id="element-681">
              <ChevronRightIcon className="h-5 w-5" data-id="element-682" />
            </button>
          </div>

          {/* Second row: Month/Week/Day toggle (centered, under date) */}
          <div className="flex gap-2 justify-center" data-id="element-683">
            <CalendarViewSelector currentView="day" onViewChange={onViewChange} isDark={isDark} availableViews={['day', 'week', 'month']} data-id="element-684" />
          </div>
        </div>
      </div>

      {/* Day View Grid */}
      <div className={`flex-1 flex flex-col rounded-lg border ${isDark ? 'border-navy-900/70' : 'border-gray-200'} overflow-hidden`} data-id="element-685">
        {/* Scrollable Time Grid */}
        <div ref={scrollContainerRef} className={`flex-1 overflow-y-auto ${isDark ? 'bg-navy-800' : ''}`} style={{
        overscrollBehavior: 'contain'
      }} data-id="element-686">
          <div ref={timeGridRef} className="relative flex" style={{
          minHeight: `${timeSlots.length * hourHeight}px`
        }} data-id="element-687">
            {/* Time labels column */}
            <div className="w-20 flex-shrink-0" data-id="element-688">
              {timeSlots.map(hour => <div key={hour} className={`h-[60px] flex items-center justify-end pr-3 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-689">
                  {getFormattedTime(hour)}
                </div>)}
            </div>

            {/* Day column */}
            <div className="flex-1 relative" data-id="element-690">
              {/* Hour cells */}
              {timeSlots.map(hour => <div key={hour} className={`h-[60px] ${isDark ? 'border-t border-navy-900/70' : 'border-t border-gray-200/70'} 
                    ${isDark ? 'hover:bg-navy-700/30' : 'hover:bg-gray-50'} cursor-pointer calendar-day-slot`} onClick={() => {
              // CRITICAL: Check popover state FIRST (same guard as Month/Week view)
              // If lesson popover is open, close it and don't trigger time slot click
              if (popoverEvent && onClosePopover) {
                onClosePopover();
                return;
              }
              // Popover is closed, proceed with normal time slot click
              onTimeSlotClick(selectedDate, hour, 0);
            }} data-id="element-691"></div>)}

              {/* Current time indicator */}
              {isCurrentDay && currentTimePosition !== null && <div className="absolute left-0 right-0 z-20 pointer-events-none" style={{
              top: `${currentTimePosition}px`
            }} data-id="element-692">
                  <div className="flex items-center" data-id="element-693">
                    <div className="w-2 h-2 rounded-full bg-deep-pink" data-id="element-694"></div>
                    <div className="flex-1 h-0.5 bg-deep-pink" data-id="element-695"></div>
                  </div>
                </div>}

              {/* Render lessons */}
              <div className="absolute top-0 left-0 right-0 bottom-0 pointer-events-none" data-id="element-696">
                {renderLessons()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-4" data-id="element-697">
        <div className="flex items-center" data-id="element-698">
          <div className="w-3 h-3 rounded-full bg-[#10B981] mr-2" data-id="element-699"></div>
          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-700">
            Confirmed
          </span>
        </div>
        <div className="flex items-center" data-id="element-701">
          <div className="w-3 h-3 rounded-full bg-[#3B82F6] mr-2" data-id="element-702"></div>
          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-703">
            Completed
          </span>
        </div>
        <div className="flex items-center" data-id="element-704">
          <div className="w-3 h-3 rounded-full bg-[#EAB308] mr-2" data-id="element-705"></div>
          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-706">
            Pending
          </span>
        </div>
        <div className="flex items-center" data-id="element-707">
          <div className="w-3 h-3 rounded-full bg-[#EF4444] mr-2" data-id="element-708"></div>
          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-709">
            Canceled
          </span>
        </div>
      </div>
    </div>;
};